export const SUPPORTED_FILE_EXTENSIONS = [
  'xlsx',
  'xlsm',
  'docx',
  'docm',
  'pptx',
  'pptm',
  'zip',
  'pdf',
  'odt',
] as const;

export const SUPPORTED_FILE_ACCEPT = SUPPORTED_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',');
export const SUPPORTED_FORMATS_LABEL = 'XLSX, XLSM, DOCX, DOCM, PPTX, PPTM, ZIP, PDF, or ODT';

const supportedExtensionSet = new Set<string>(SUPPORTED_FILE_EXTENSIONS);
const legacyExtensionSet = new Set(['xls', 'doc', 'ppt']);

export interface EngineCapability {
  engine: string;
  formats: string;
  analyze: string;
  safeRepair: string;
  contentRescue: string;
  encryption: string;
  boundaries: string;
}

export const ENGINE_CAPABILITIES: readonly EngineCapability[] = [
  {
    engine: 'Excel',
    formats: 'XLSX · XLSM',
    analyze: 'Package, workbook, sheets, relationships, styles, shared strings, formulas, macros, protection, external links, and media.',
    safeRepair: 'Missing worksheet relationships and invalid cell style indices, using two deterministic rules.',
    contentRescue: 'CSV per sheet, media, recovery report, and package inventory.',
    encryption: 'Detected. Password-to-open files are refused, not unlocked.',
    boundaries: 'No XLS, password recovery, VBA execution, or reconstruction of formulas, charts, pivots, and other document content.',
  },
  {
    engine: 'Word',
    formats: 'DOCX · DOCM',
    analyze: 'Document stories, relationships, properties, protection, media, attachments, external links, and macros.',
    safeRepair: 'Package-root, singleton support-part, and header or footer relationships only when an existing target proves a unique link.',
    contentRescue: 'TXT, Markdown, inert HTML, tables, stories, media, attachments, metadata, and raw XML.',
    encryption: 'Detected. Password-to-open files are refused, not unlocked.',
    boundaries: 'No DOC, password recovery, layout rendering, invented content, or VBA execution.',
  },
  {
    engine: 'PowerPoint',
    formats: 'PPTX · PPTM',
    analyze: 'Slide order and hidden state, text, tables, notes, masters, layouts, themes, charts, media, transitions, animations, and macros.',
    safeRepair: 'Package-root, presentation-part, slide, and master relationships only when the missing link is uniquely inferable.',
    contentRescue: 'TXT, Markdown, inert HTML, notes, images, audio, video, charts, embedded workbooks, metadata, and raw XML.',
    encryption: 'Detected. Password-to-open files are refused, not unlocked.',
    boundaries: 'No PPT, password recovery, slide rendering, invented slide order or content, or VBA execution.',
  },
  {
    engine: 'ZIP',
    formats: 'ZIP',
    analyze: 'EOCD, central directory, local headers, paths, methods, sizes, CRC32, and hostile-input limits.',
    safeRepair: 'Central-directory and EOCD rebuild only when every local entry has safe, authoritative boundaries and validates.',
    contentRescue: 'Folder-preserving ZIP of independently validated stored or Deflate entries, with an exclusion inventory.',
    encryption: 'Encrypted entries are detected and excluded or refused, never decrypted.',
    boundaries: 'No ZIP64, multi-volume, data-descriptor recovery, unsafe or duplicate paths, unsupported methods, or uncertain gaps.',
  },
  {
    engine: 'PDF',
    formats: 'PDF',
    analyze: 'Header and EOF, cross-reference structure, objects, page tree, metadata, images, attachments, forms, actions, JavaScript, and encryption.',
    safeRepair: 'Classic cross-reference, trailer, and EOF rebuild only when object, stream, catalog, and page-tree boundaries are proven.',
    contentRescue: 'Best-effort page text and inventory, original JPEG or JPX streams, attachments, metadata, raw objects, and active-content evidence.',
    encryption: 'Detected. Encrypted content is not decrypted or extracted.',
    boundaries: 'No passwords, OCR, custom-font decoding, compressed-object rewriting, JavaScript execution, or ambiguous reconstruction.',
  },
  {
    engine: 'OpenDocument Text',
    formats: 'ODT',
    analyze: 'Mimetype, manifest, XML, text structure, media, links, objects, styles, metadata, tracked changes, and ODF encryption.',
    safeRepair: 'Canonical mimetype normalization and type-proven manifest reconstruction when every required entry validates.',
    contentRescue: 'TXT, Markdown, inert HTML, unencrypted media, embedded data, metadata, and raw XML.',
    encryption: 'Detected. Encrypted entries are not decrypted or exported.',
    boundaries: 'No ODS, ODP, password recovery, page-layout reproduction, object semantics, formulas, or guessed XML repair.',
  },
] as const;

export function isSupportedFileName(fileName: string): boolean {
  return supportedExtensionSet.has(fileExtension(fileName));
}

export function unsupportedFileMessage(fileName: string): string | null {
  const extension = fileExtension(fileName);
  if (supportedExtensionSet.has(extension)) return null;
  if (legacyExtensionSet.has(extension)) {
    return `${fileName} uses the legacy .${extension.toUpperCase()} format. MendByte does not process legacy XLS, DOC, or PPT files. Convert a copy to a supported modern format first.`;
  }
  return `${fileName} is not a supported file type. Choose ${SUPPORTED_FORMATS_LABEL}.`;
}

function fileExtension(fileName: string): string {
  const match = /\.([^.]+)$/.exec(fileName.trim());
  return match?.[1]?.toLowerCase() ?? '';
}
